import {
  forwardRef, ChangeEventHandler, MutableRefObject, memo,
} from 'react';
import classNames from 'classnames';
import { S2, S3, AB3 } from '../../theme';
import Box from '../Box';
import Grid, { IGridProps } from '../Grid';
import Typography from '../Typography';
import { ImportIcon } from '../../icons';
import { mergeSX } from '../../services/helpers';

export interface IDragAndDropLocalization {
  chooseFile: string;
  orDragAndDrop: string;
  dropFileHere: string;
}

export interface IDragAndDropProps extends IGridProps {
  localization: IDragAndDropLocalization;
  minHeight?: string | number;
  height?: string | number;
  maxHeight?: string | number;
  canDrop: boolean;
  onChooseFileClick: () => void;
  onFileInputChange: ChangeEventHandler<HTMLInputElement>;
  fileInputRef: MutableRefObject<HTMLInputElement | null>;
}

export interface IBuildSXParams {
  minHeight: string | number;
  height: string | number;
  maxHeight?: string | number;
}

const TYPOGRAPHY_SX = {
  textDecoration: 'underline',
  cursor: 'pointer',
  color: AB3,
};

const CSV_FILE_INPUT_SX = { display: { xs: 'none' } };

const ICON_HEIGHT = 24;
const BORDER_WIDTH = 2;
const MIN_HEIGHT = ICON_HEIGHT + BORDER_WIDTH * 2;

const buildSX = ({ minHeight, height, maxHeight }: IBuildSXParams) => ({
  border: `${BORDER_WIDTH}px dashed ${S3}`,
  borderRadius: 4,
  minHeight,
  height,
  maxHeight,
  color: AB3,
  '&.canDrop': {
    backgroundColor: S2,
    '& .MuiTypography-root': {
      fontWeight: 'bold',
    },
  },
});

const DragAndDropContainer = forwardRef<HTMLDivElement, IDragAndDropProps>((props, ref) => {
  const {
    canDrop,
    minHeight = MIN_HEIGHT,
    height = '100%',
    maxHeight,
    localization,
    fileInputRef,
    onChooseFileClick,
    onFileInputChange,
    sx,
    className,
    onClick,
  } = props;
  const {
    chooseFile = 'Choose a file',
    orDragAndDrop = 'or drag-and-drop',
    dropFileHere = 'Drop here',
  } = localization;

  return (
    <Grid
      container
      ref={ref}
      sx={mergeSX(buildSX({ minHeight, height, maxHeight }), sx)}
      className={classNames(className, { canDrop })}
    >
      <Grid container justifyContent="center" alignItems="center">
        {
          !canDrop && (
            <>
              <Grid item>
                <Typography
                  variant="subtitle1"
                  sx={TYPOGRAPHY_SX}
                  onClick={onChooseFileClick}
                >
                  {chooseFile}
                </Typography>
              </Grid>
              <Grid item>
                <Typography>
                  &nbsp;
                  {orDragAndDrop}
                </Typography>
              </Grid>
              <Box mr={1} />
              <ImportIcon height={ICON_HEIGHT} />
            </>
          )
        }
        {canDrop && <Typography>{dropFileHere}</Typography>}
      </Grid>
      <Box sx={CSV_FILE_INPUT_SX}>
        <input
          accept="text/csv"
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onFileInputChange}
          onClick={onClick}
        />
      </Box>
    </Grid>
  );
});

export default memo(DragAndDropContainer);
